import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePageSEO } from '../hooks/usePageSEO';
import { breadcrumbSchema, serviceSchema, faqSchema } from '../utils/structuredData';
import { ArrowRightIcon, CheckCircle2Icon, StarIcon, AlertCircleIcon, SparklesIcon } from 'lucide-react';
import { SERVICES, getServiceBySlug } from '../utils/servicesData';
function StarRow() {
  return <div className="flex items-center gap-1" data-id="element-1460" data-mp="2dz3zppajyb60">
      {Array.from({
      length: 5
    }).map((_, i) => <StarIcon key={i} className="w-4 h-4 text-[#D4AF37] fill-[#D4AF37]" data-id="element-1461" data-mp="2iz9mos5vn9hr" data-mpi={i} />)}
    </div>;
}
export function ServiceDetailPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const service = getServiceBySlug(slug);
  usePageSEO({
    canonical: `/services/${slug}`,
    keywords: service ? `${service.title.toLowerCase()}, Shopify ${service.title.toLowerCase()}, Shopify agency services, ecommerce ${service.title.toLowerCase()}` : 'Shopify services',
    jsonLd: service ? [breadcrumbSchema([{
      name: 'Home',
      path: '/'
    }, {
      name: 'Services',
      path: '/services'
    }, {
      name: service.title,
      path: `/services/${slug}`
    }]), serviceSchema({
      name: service.title,
      description: service.heroSubhead,
      path: `/services/${slug}`
    }), ...(service.faqs && service.faqs.length > 0 ? [faqSchema(service.faqs.map(f => ({
      question: f.q,
      answer: f.a
    })))] : [])] : undefined,
    title: service ? `${service.title} | Design Flow Digitals` : 'Services | Design Flow Digitals',
    description: service ? `${service.heroHeadline}. ${service.heroSubhead}` : 'Premium Shopify services by Design Flow Digitals.'
  });
  if (!service) {
    return <div className="pt-40 pb-32 px-6 text-center" data-id="element-1462" data-mp="1vknmjmp2z46i">
        <h1 className="text-4xl font-bold mb-4" data-id="element-1463" data-mp="20lb8vv2d6wue">Service not found</h1>
        <p className="text-gray-400 mb-8" data-id="element-1464" data-mp="20iyrpk4fkqie">
          The service you’re looking for doesn’t exist.
        </p>
        <Link to="/services" className="inline-flex items-center gap-2 bg-gradient-gold text-black px-8 py-4 rounded-full font-bold gold-glow-hover transition-all hover:scale-105" data-id="element-1465" data-mp="25hs7jj76wtlq">
          View all services <ArrowRightIcon className="w-5 h-5" data-id="element-1466" data-mp="1hu0jjicaes4m" />
        </Link>
      </div>;
  }
  const Icon = service.icon;
  const otherServices = SERVICES.filter(s => s.slug !== service.slug);
  return <div className="w-full" data-id="element-1467" data-mp="p64t2vdb9wga">
      {/* Hero */}
      <section className="relative min-h-[80vh] flex items-center pt-32 pb-20 overflow-hidden" data-id="element-1468" data-mp="p5d3c16easxx">
        <div className="absolute inset-0 bg-grid-pattern opacity-10 pointer-events-none" data-id="element-1469" data-mp="u3eoqsran4ck" />
        <div className="absolute top-1/4 right-1/4 w-[34rem] h-[34rem] bg-[#D4AF37]/10 rounded-full blur-[130px] animate-pulse-glow pointer-events-none" data-id="element-1470" data-mp="37dxvpi08nifn" />

        <div className="max-w-5xl mx-auto px-6 md:px-12 relative z-10 text-center flex flex-col items-center" data-id="element-1471" data-mp="2yeaw91r2kmp8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-8 border-[#D4AF37]/30" data-id="element-1472" data-mp="2tfzp4ys5sqap">
            <SparklesIcon className="w-4 h-4 text-[#D4AF37]" data-id="element-1473" data-mp="2ohet7bjxwss2" />
            <span className="text-sm font-semibold text-[#F3E5AB] tracking-wide uppercase" data-id="element-1474" data-mp="2jpwvqxh4labu">
              {service.eyebrow}
            </span>
          </div>

          <div className="w-20 h-20 rounded-3xl bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center mb-8" data-id="element-1475" data-mp="2fobbus66bvs5">
            <Icon className="w-10 h-10 text-[#D4AF37]" data-id="element-1476" data-mp="25pvsjbde1qt7" />
          </div>

          <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight" data-id="element-1477" data-mp="25rzwftbaepth">
            {service.heroHeadline}
          </h1>
          <p className="text-lg md:text-xl text-gray-400 max-w-2xl mb-8 leading-relaxed" data-id="element-1478" data-mp="4no14pp5nui2">
            {service.heroSubhead}
          </p>

          <div className="flex items-center gap-3 mb-10" data-id="element-1479" data-mp="9lpjf8erbpsh">
            <StarRow data-id="element-1480" data-mp="3r38qyrz25y6s" />
            <span className="text-sm text-gray-400" data-id="element-1481" data-mp="3r40ldzgx5pvh">
              Trusted by 1,000+ Shopify merchants
            </span>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-4" data-id="element-1482" data-mp="3w0im8o94p42j">
            <Link to="/contact" className="w-full sm:w-auto bg-gradient-gold text-black px-8 py-4 rounded-full font-bold text-lg gold-glow-hover transition-all duration-300 flex items-center justify-center gap-2 group hover:scale-105" data-id="element-1483" data-mp="3i1iuv8u8eb2o">
              Start Your Project
              <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-1484" data-mp="3d492bkbjpjam" />
            </Link>
            <Link to="/portfolio" className="w-full sm:w-auto px-8 py-4 rounded-full font-bold text-lg text-white border border-white/20 hover:bg-white/5 transition-colors" data-id="element-1485" data-mp="16dszdus0m5g3">
              See Our Work
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Strip */}
      <section className="border-y border-white/5 bg-[#050505]" data-id="element-1486" data-mp="35jygpjaaes29">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-1487" data-mp="35l9ixeo33rr9">
          {service.stats.map(s => <div key={s.label} className="text-center" data-id="element-1488" data-mp="3ekd4cpaq4j8x">
              <p className="text-3xl md:text-4xl font-bold text-white mb-1" data-id="element-1489" data-mp="3eitmiu9f6051">
                {s.value}
              </p>
              <p className="text-sm text-gray-500" data-id="element-1490" data-mp="3ale25cgdn0l6">{s.label}</p>
            </div>)}
        </div>
      </section>

      {/* Problem → Outcome */}
      <section className="section-padding bg-black" data-id="element-1491" data-mp="3jl10tqhq1saj">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto" data-id="element-1492" data-mp="1u4rrxu39ahc0">
          <div className="glass-card p-10 border-white/10" data-id="element-1493" data-mp="1u6mhjlph2xmf">
            <div className="w-12 h-12 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-6" data-id="element-1494" data-mp="1p702r7tzx9bi">
              <AlertCircleIcon className="w-6 h-6 text-red-400" data-id="element-1495" data-mp="3pa11png0wbbr" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4" data-id="element-1496" data-mp="3u7klt3zejibm">The problem</h2>
            <p className="text-lg text-gray-400 leading-relaxed" data-id="element-1497" data-mp="22752331u6y5">
              {service.problem}
            </p>
          </div>
          <div className="glass-card p-10 border-[#D4AF37]/20 bg-gradient-to-br from-[#D4AF37]/5 to-transparent" data-id="element-1498" data-mp="73mknfmgjl95">
            <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center mb-6" data-id="element-1499" data-mp="bzlg1n67e3j2">
              <CheckCircle2Icon className="w-6 h-6 text-[#D4AF37]" data-id="element-1500" data-mp="lqpnm1tjum3v" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4" data-id="element-1501" data-mp="pskvjp5zjxqm">
              How we fix it
            </h2>
            <p className="text-lg text-gray-300 leading-relaxed" data-id="element-1502" data-mp="usfkvoa44f9k">
              {service.outcome}
            </p>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="section-padding bg-[#020202] border-y border-white/5" data-id="element-1503" data-mp="19unulh7srsk7">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1504" data-mp="19skvm7oo2p3c">
          <h2 className="text-4xl font-bold mb-6" data-id="element-1505" data-mp="14u9jfqawg2hy">What’s included</h2>
          <p className="text-xl text-gray-400" data-id="element-1506" data-mp="zvznvel9izvt">
            Everything you need, delivered to a premium standard.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto" data-id="element-1507" data-mp="vwz5fx6p5z9c">
          {service.deliverables.map(d => <div key={d.title} className="glass-card p-8 border-white/10 hover:border-[#D4AF37]/30 transition-colors" data-id="element-1508" data-mp="1nltyrqm0uedf">
              <CheckCircle2Icon className="w-6 h-6 text-[#D4AF37] mb-5" data-id="element-1509" data-mp="1itiz8ov70ule" />
              <h3 className="text-lg font-bold text-white mb-2" data-id="element-1510" data-mp="1et8qx5jxhs3d">{d.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed" data-id="element-1511" data-mp="2bc6dbkbrv9cz">{d.desc}</p>
            </div>)}
        </div>
      </section>

      {/* Benefits + Engagement */}
      <section className="section-padding bg-black" data-id="element-1512" data-mp="2q9i93adn5flg">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center max-w-6xl mx-auto" data-id="element-1513" data-mp="2q7npbze3vtiy">
          <div data-id="element-1514" data-mp="2v19rgxmn8f0i">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-1515" data-mp="2v1rvwhjglzji">
              Why merchants choose us for {service.title.toLowerCase()}
            </h2>
            <ul className="space-y-4" data-id="element-1516" data-mp="2lax4yzv9qbrq">
              {service.benefits.map(b => <li key={b} className="flex items-start gap-3 text-gray-300" data-id="element-1517" data-mp="390rkjp6z2efo">
                  <CheckCircle2Icon className="w-5 h-5 text-[#D4AF37] shrink-0 mt-0.5" data-id="element-1518" data-mp="3dzckdnibaw3l" />
                  <span className="text-lg" data-id="element-1519" data-mp="2z1siipgpph1n">{b}</span>
                </li>)}
            </ul>
          </div>

          <div className="glass-card p-10 border-[#D4AF37]/20 bg-gradient-to-b from-[#D4AF37]/5 to-transparent text-center" data-id="element-1520" data-mp="33zu0f72ejabj">
            <p className="text-sm font-bold text-[#D4AF37] uppercase tracking-widest mb-3" data-id="element-1521" data-mp="1nktxva0ipbuq">
              {service.engagement.label}
            </p>
            <p className="text-4xl font-bold text-white mb-4" data-id="element-1522" data-mp="anl9qegathpo">
              {service.engagement.value}
            </p>
            <p className="text-gray-400 mb-8 leading-relaxed" data-id="element-1523" data-mp="5pjphq74zccm">
              {service.engagement.note}
            </p>
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 w-full bg-gradient-gold text-black px-8 py-4 rounded-full font-bold gold-glow-hover transition-all hover:scale-105" data-id="element-1524" data-mp="jn8ena88l5nh">
              Get a free proposal <ArrowRightIcon className="w-5 h-5" data-id="element-1525" data-mp="3j46k9zokwiuw" />
            </Link>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section-padding bg-[#020202] border-y border-white/5" data-id="element-1526" data-mp="r8h7jwhp9wi">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1527" data-mp="3s3a455vkl2ag">
          <h2 className="text-4xl font-bold mb-6" data-id="element-1528" data-mp="34eikj2w1ps9a">Our process</h2>
          <p className="text-xl text-gray-400" data-id="element-1529" data-mp="34cxsbjx7elel">
            A proven, transparent workflow from first call to lasting results.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto" data-id="element-1530" data-mp="27jj5q187yec8">
          {service.process.map(p => <div key={p.step} className="group glass-card p-8 border-white/10 relative overflow-hidden hover:border-[#D4AF37]/30 transition-colors" data-id="element-1531" data-mp="22kzhtva2esh8">
              <div className="text-5xl font-bold text-[#D4AF37]/15 mb-6 group-hover:text-[#D4AF37]/25 transition-colors" data-id="element-1532" data-mp="1tizzmznbdicp">
                {p.step}
              </div>
              <h3 className="text-lg font-bold text-white mb-3" data-id="element-1533" data-mp="1jtgfzrrs1kgs">{p.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed" data-id="element-1534" data-mp="1euc8d5ki3usk">{p.desc}</p>
            </div>)}
        </div>
      </section>

      {/* Testimonial */}
      <section className="section-padding bg-black" data-id="element-1535" data-mp="1auv1q8tc8rlt">
        <div className="max-w-3xl mx-auto text-center" data-id="element-1536" data-mp="15u71cwj7yyex">
          <StarRow data-id="element-1537" data-mp="15w0cao0v84sq" />
          <p className="text-2xl md:text-3xl font-medium text-white leading-relaxed my-8" data-id="element-1538" data-mp="10v46g4wye8ko">
            “{service.testimonial.quote}”
          </p>
          <p className="font-bold text-white" data-id="element-1539" data-mp="3chehkdimu9ta">{service.testimonial.name}</p>
          <p className="text-sm text-gray-500" data-id="element-1540" data-mp="3hghknsf1y5ym">{service.testimonial.role}</p>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-[#020202] border-y border-white/5" data-id="element-1541" data-mp="3mgvjcjurk3jz">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1542" data-mp="3qgxo51vspd3h">
          <h2 className="text-4xl font-bold mb-6" data-id="element-1543" data-mp="2troihe7u71vk">
            Frequently asked questions
          </h2>
        </div>
        <div className="max-w-3xl mx-auto space-y-5" data-id="element-1544" data-mp="2yq9ndqy1a9qc">
          {service.faqs.map(f => <div key={f.q} className="glass-card p-6 border-white/10" data-id="element-1545" data-mp="32ruvz03jc1uq">
              <h3 className="text-lg font-bold text-white mb-2" data-id="element-1546" data-mp="32qjputmmai0g">{f.q}</h3>
              <p className="text-gray-400 leading-relaxed" data-id="element-1547" data-mp="37ke8dtqhkqxy">{f.a}</p>
            </div>)}
        </div>
      </section>

      {/* Other services */}
      <section className="section-padding bg-black" data-id="element-1548" data-mp="2f2a8q50cmifn">
        <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-1549" data-mp="2f0pfqjton7jw">
          <h2 className="text-3xl font-bold mb-4" data-id="element-1550" data-mp="3ico5kxfq0e24">Explore more services</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto" data-id="element-1551" data-mp="3dfegxk05o6fc">
          {otherServices.map(s => {
          const OtherIcon = s.icon;
          return <Link key={s.slug} to={`/services/${s.slug}`} className="group glass-card p-8 border-white/10 hover:border-[#D4AF37]/30 transition-all hover:-translate-y-1" data-id="element-1552" data-mp="3n4o7gvfcph1c">
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center mb-6" data-id="element-1553" data-mp="2uh267ttliohn">
                  <OtherIcon className="w-6 h-6 text-[#D4AF37]" data-id="element-1554" data-mp="38d71ulk14a8s" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2 group-hover:text-[#D4AF37] transition-colors" data-id="element-1555" data-mp="34g16op47jdtk">
                  {s.title}
                </h3>
                <span className="inline-flex items-center gap-1 text-sm text-[#D4AF37] font-medium" data-id="element-1556" data-mp="jmo09ihmdfos">
                  Learn more{' '}
                  <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-1557" data-mp="r9bcel2pbplt" />
                </span>
              </Link>;
        })}
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 px-6 text-center bg-gradient-to-b from-transparent to-[#D4AF37]/5" data-id="element-1558" data-mp="m85jam54q0br">
        <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-1559" data-mp="19xpzix9wejhc">
          Ready to get started?
        </h2>
        <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto" data-id="element-1560" data-mp="1ewji3441l0od">
          Book a free consultation and we’ll map out exactly how we’ll grow your
          Shopify store.
        </p>
        <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-gradient-gold text-black px-10 py-5 rounded-full font-bold text-lg gold-glow-hover transition-all duration-300 hover:scale-105" data-id="element-1561" data-mp="156zy1v4lf0rx">
          Start Your Project <ArrowRightIcon className="w-5 h-5" data-id="element-1562" data-mp="1xovjnomvfxxg" />
        </Link>
      </section>
    </div>;
}